// Section: Preview Calculator (gated by partsFinancialEngine)
import { useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useFeatureFlag } from '@/hooks/useFeatureFlags';
import { computeEstimate, type FeeRule, type TaxabilityCategories } from '@/lib/parts-financial/calculator';

interface Props {
  feeRules: FeeRule[];
  categories: TaxabilityCategories;
  combinedRate: number;
}

const SCENARIOS = {
  brake: { label: 'Brake Job', partsSubtotal: 220, laborSubtotal: 180, tireUnits: 0 },
  tires: { label: 'Tires (set of 4)', partsSubtotal: 600, laborSubtotal: 120, tireUnits: 4 },
  diag:  { label: 'Diagnostic', partsSubtotal: 0, laborSubtotal: 165, tireUnits: 0 },
};

type ScenarioKey = keyof typeof SCENARIOS;

const fmt = (n: number) => n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

export function PreviewCalculatorSection({ feeRules, categories, combinedRate }: Props) {
  const enabled = useFeatureFlag('partsFinancialEngine');
  const [scenario, setScenario] = useState<ScenarioKey>('brake');

  const result = useMemo(() => {
    const s = SCENARIOS[scenario];
    return computeEstimate({
      partsSubtotal: s.partsSubtotal,
      laborSubtotal: s.laborSubtotal,
      tireUnits: s.tireUnits,
      feeRules,
      taxRules: { categories },
      taxSettings: { combined_rate: combinedRate },
    });
  }, [scenario, feeRules, categories, combinedRate]);

  if (!enabled) return null;

  const s = SCENARIOS[scenario];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Preview Calculator</CardTitle>
        <CardDescription>
          Estimated totals for sample tickets — for testing your configuration. Not a quoted price.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-2">
          {(Object.keys(SCENARIOS) as ScenarioKey[]).map((k) => (
            <Button
              key={k}
              type="button"
              variant={scenario === k ? 'default' : 'outline'}
              size="sm"
              onClick={() => setScenario(k)}
            >
              {SCENARIOS[k].label}
            </Button>
          ))}
        </div>

        <div className="rounded-md border p-4 space-y-2 bg-muted/20">
          <div className="flex justify-between text-sm"><span>Parts subtotal</span><span>{fmt(s.partsSubtotal)}</span></div>
          <div className="flex justify-between text-sm"><span>Labor subtotal</span><span>{fmt(s.laborSubtotal)}</span></div>
          {result.feesBreakdown.length > 0 && (
            <div className="pt-2 border-t space-y-1">
              {result.feesBreakdown.map((f, i) => (
                <div key={i} className="flex justify-between text-sm">
                  <span className="text-muted-foreground">
                    {f.label}{!f.taxable && <span className="ml-1 text-xs">(non-taxable)</span>}
                  </span>
                  <span>{fmt(f.amount)}</span>
                </div>
              ))}
            </div>
          )}
          <div className="flex justify-between text-sm pt-2 border-t">
            <span>Fees total</span><span>{fmt(result.feesTotal)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span>Taxable base</span><span>{fmt(result.taxableBase)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span>Tax @ {(combinedRate * 100).toFixed(3)}%</span><span>{fmt(result.tax)}</span>
          </div>
          <div className="flex justify-between font-semibold pt-2 border-t">
            <span>Estimated total</span><span>{fmt(result.total)}</span>
          </div>
        </div>
        <p className="text-xs text-muted-foreground italic">
          Estimated values for internal verification only. Actual ticket pricing will use the same calculator in a future phase.
        </p>
      </CardContent>
    </Card>
  );
}
